import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { Typography, Spin, Alert, Button, message, Modal } from 'antd';
import { Link } from 'react-router-dom';
import apiClient from '../../../apis/ApiInterceptor';
import { unfreezePlan } from '../../../apis/plansApi';
import ConfirmActivatePlan from './ConfirmActivatePlanModal';
import FreezePLanModal from '../../../components/FreezePlanModal';
import { checkAutoPermissions, isFeatureEnabledForBranch } from '../../../apis/featureApi';
import Features from '../../../enums/features';

dayjs.extend(utc);

const { Title } = Typography;

const CurrentPlan = ({ userId, className, refreshKey, setRefreshKey }) => {
    const [openFreezePlan, setOpenFreezePlan] = useState(false);
    const [userPlans, setUserPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showActivatePlanModal, setShowActivatePlanModal] = useState(false);
    const [selectedPlanToActivate, setSelectedPlanToActivate] = useState(null);
    const [selectedPlanToFreeze, setSelectedPlanToFreeze] = useState(null);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [isFreezePlanEnabled, setIsFreezePlanEnabled] = useState(false);
    
    useEffect(() => {
        if (!userId) {
            setError("No se proporcionó un ID de usuario.");
            setLoading(false);
            return;
        }

        const fetchUserPlans = async () => {
            try {
                setLoading(true);
                const response = await apiClient.get(`/users/${userId}/plans`); // TODO: move this to api
                setUserPlans(response.data || []);
                setLoading(false);
            } catch (err) {
                setError("Error al cargar los planes del usuario.");
                setLoading(false);
            }
        };

        fetchUserPlans();
    }, [userId, refreshKey]);

    useEffect(() => {
      const checkAuthorization = async () => {
        setLoading(true);
        const isAuthorized = await checkAutoPermissions(Features.SEE_OTHERS_CURRENT_PLAN, userId);
        if (!isAuthorized) {
            const features = JSON.parse(localStorage.getItem('user_features') || '{}');
            setIsAuthorized(features[Features.SEE_OTHERS_CURRENT_PLAN] || false);
        }else{
            setIsAuthorized(true);
        }
        setLoading(false);
      };
      checkAuthorization();
    }, [userId]);

    useEffect(() => {

        const loadFeatures = async () => {
            const featureEnabled = await isFeatureEnabledForBranch(Features.FREEZE_PLAN);
            setIsFreezePlanEnabled(featureEnabled);
        };
        loadFeatures();
    }, []);

    if (loading) {
        return (
            <div className="text-center mt-3">
                <Spin size="large" />
            </div>
        );
    }

    if (!isAuthorized) {
        return null
    }

    const handleActivatePlan = (plan) => {
        setShowActivatePlanModal(true);
        setSelectedPlanToActivate(plan);
    };

    const handleFreezePlan = (plan) => {
        setOpenFreezePlan(true);
        setSelectedPlanToFreeze(plan);
    };

    const handleUnfreezePlan = (plan) => {
        Modal.confirm({
            title: "¿Descongelar Plan?",
            content: `¿Estás segura de descongelar el plan "${plan.name}"? El plan volverá a estar activo a partir de hoy.`,
            okText: "Descongelar",
            cancelText: "Cancelar",
            onOk: async () => {
                try {
                    const response = await unfreezePlan(plan.id);
                    if (response.status === 200) {
                        message.success(response.data?.message || "Plan descongelado exitosamente.");
                        if (setRefreshKey) {
                            setRefreshKey(prev => prev + 1);
                        }
                    }
                } catch (error) {
                    const backendMessage = error.response?.data?.message || error.response?.data?.error;
                    message.error(backendMessage || "Error al descongelar el plan. Contacta a soporte.");
                }
            }
        });
    };

    const successFreezePlan = () => {
        setRefreshKey(prev => prev + 1);
        message.success("Plan congelado exitosamente.");
    };

    if (error) {
        return (
            <div className={`${className} text-center`}>
                <Alert message="Error" description={error} type="error" showIcon />
            </div>
        );
    }

    return (
        <>
        <div className={`col-11 ${className}`}>
            <Title level={3} className="text-center">Mis Planes</Title>
            {!userPlans.length ? (
                <div className="text-center my-4">
                    <p>Aún no tienes un plan</p>
                    <Link to="/plans">
                        <Button type="primary">
                            Ver Planes
                        </Button>
                    </Link>
                </div>
            ) : (
                userPlans.map((plan, idx) => (
                    <div key={plan.id || idx} className="themed-block text-center text-md-left d-md-flex p-3 mx-auto mb-3">
                        <div>
                            <Title level={4} className="d-block my-2">{plan.name}</Title>
                            {plan.remaining_shared_classes && (
                                <p className="d-block my-1">
                                    <span className="font-weight-bold">Clases Restantes: </span>
                                    {plan.remaining_shared_classes}
                                </p>
                            )}
                            {plan.frozen_from && (
                                <p className="d-block my-1">
                                    <span className="font-weight-bold">Congelado: </span>
                                    {dayjs(plan.frozen_from).format('DD/MM/YYYY')} - {plan.frozen_to ? dayjs(plan.frozen_to).format('DD/MM/YYYY') : 'Indeterminado'}
                                </p>
                            )}
                            <p className="d-block my-1">
                                <span className="font-weight-bold">Válido desde: </span>
                                {dayjs.utc(plan.activation_date).tz(dayjs.tz.guess()).format('DD/MM/YYYY')}
                            </p>
                            <p className="d-block my-1">
                                <span className="font-weight-bold">Válido hasta: </span>
                                {dayjs.utc(plan.expiration_date).tz(dayjs.tz.guess()).format('DD/MM/YYYY')}
                            </p>
                        </div>
                        {/* Show freeze option if plan is active */}
            
                        { isFreezePlanEnabled && dayjs.utc(plan.activation_date).isBefore(dayjs.utc()) && !plan.frozen_from && (
                            <div className="ml-auto my-3">
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={() => handleFreezePlan(plan)}
                                >
                                    Congelar Plan
                                </button>
                            </div>
                        )}
                        {/* Show unfreeze option if plan is frozen */}
                        { isFreezePlanEnabled && plan.frozen_from && (
                            <div className="ml-auto my-3">
                                <button
                                    type="button"
                                    className="btn btn-warning"
                                    onClick={() => handleUnfreezePlan(plan)}
                                >
                                    Descongelar Plan
                                </button>
                            </div>
                        )}
                        {/* Show activate option if plan is not yet active */}
                        {dayjs.utc(plan.activation_date).isAfter(dayjs.utc()) && (
                            <div className="ml-auto my-3">
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={() => handleActivatePlan(plan)}
                                >
                                    Activar Plan
                                </button>
                            </div>
                        )}
                    </div>
                ))
            )}
        </div>
        <ConfirmActivatePlan
            show={showActivatePlanModal}
            plan={selectedPlanToActivate}
            onClose={() => setShowActivatePlanModal(false)}
        />
        <FreezePLanModal
            open={openFreezePlan}
            userPlanId={selectedPlanToFreeze?.id}
            onSuccess={() => successFreezePlan()}
            onClose={() => setOpenFreezePlan(false)}
        />
        </>
    );
};

export default CurrentPlan;
